/**
 * Biological drawing primitives.
 *
 * Each function draws into a supplied Pixi Graphics object. Functions come
 * in pairs: static ones are called once from the factory, animated ones are
 * called every frame by the render tick (RenderableC.draw).
 *
 * Style notes:
 *   - no hard outlines; edges fade via layered low-alpha fills
 *   - palette from the spec (cyan player, magenta protein, gold sugar,
 *     green bacteria, red predators, purple DNA)
 *   - shapes reference real microbiology — glucose ring, peptide chain,
 *     rod bacillus with flagellum, double-helix DNA, amoeba pseudopods,
 *     paramecium with cilia, armored dinoflagellate with theca plates
 */

import { Graphics } from 'pixi.js';

// ---- helpers ----

function softHalo(g: Graphics, radius: number, color: number, layers = 3, alpha = 0.08): void {
  for (let i = 0; i < layers; i++) {
    const a = alpha - i * (alpha / layers);
    if (a <= 0) continue;
    g.circle(0, 0, radius * (1 + i * 0.25)).fill({ color, alpha: a });
  }
}

function mix(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const gg = Math.round(ag + (bg - ag) * t);
  const bb2 = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (gg << 8) | bb2;
}

/** Sample a wobbly closed blob around origin, N points, wobble drifts with time. */
function wobblyBlob(g: Graphics, radius: number, points: number, wobble: number, time: number, seed = 0): void {
  const pts: number[] = [];
  for (let i = 0; i < points; i++) {
    const a = (i / points) * Math.PI * 2;
    const n = Math.sin(a * 3 + time + seed) * 0.5 + Math.cos(a * 5 + time * 0.7 + seed * 1.3) * 0.5;
    const r = radius * (1 + n * wobble);
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.poly(pts);
}

/** Wavy line — useful for flagella, cilia, tails. */
function wavyLine(g: Graphics, x1: number, y1: number, x2: number, y2: number, amp: number,
                  waves: number, time: number, color: number, alpha: number, width: number, phase = 0): void {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len; // perpendicular
  const steps = 14;
  let started = false;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const w = Math.sin(t * waves * Math.PI * 2 + time + phase) * amp * t; // amplitude grows along length
    const px = x1 + dx * t + nx * w;
    const py = y1 + dy * t + ny * w;
    if (!started) { g.moveTo(px, py); started = true; }
    else g.lineTo(px, py);
  }
  g.stroke({ color, alpha, width });
}

// ---- player cell (eukaryote) ----

export interface CellDrawOpts { radius: number; fieldRadius: number; }

export function drawCellHalo(glow: Graphics, fieldRadius: number): void {
  glow.clear();
  for (let i = 0; i < 5; i++) {
    const a = 0.06 - i * 0.011;
    if (a <= 0) break;
    glow.circle(0, 0, fieldRadius * (1 + i * 0.08)).fill({ color: 0x4FD1C5, alpha: a });
  }
  glow.circle(0, 0, fieldRadius).stroke({ color: 0x4FD1C5, alpha: 0.18, width: 1 });
}

export function drawCellBody(body: Graphics, opts: CellDrawOpts, time: number, mutations: readonly string[] = []): void {
  const { radius } = opts;
  body.clear();

  // Some mutations want to draw UNDER the membrane (auras, magnetic field).
  drawUnderlays(body, radius, time, mutations);

  // outermost soft glow
  softHalo(body, radius * 1.2, 0x4FD1C5, 2, 0.18);
  // wobbly membrane
  wobblyBlob(body, radius * 1.05, 24, 0.04, time * 1.4);
  body.fill({ color: 0x2ea89c, alpha: 0.35 });
  wobblyBlob(body, radius * 0.98, 24, 0.035, time * 1.4 + 0.5);
  body.fill({ color: 0x4FD1C5, alpha: 0.7 });
  // cytoplasm highlight (top-left)
  body.circle(-radius * 0.25, -radius * 0.35, radius * 0.55).fill({ color: 0xB2F5EA, alpha: 0.18 });

  // chloroplasts sit inside the cytoplasm, before mitochondria so they're a
  // background green wash. Drawn here when the mutation is equipped.
  if (mutations.includes('chloroplasts')) drawChloroplasts(body, radius, time);

  // mitochondria — 4 little oval organelles drifting around the cytoplasm
  const mitoCount = 4;
  for (let i = 0; i < mitoCount; i++) {
    const a = (i / mitoCount) * Math.PI * 2 + time * 0.3;
    const r = radius * 0.55;
    const mx = Math.cos(a) * r;
    const my = Math.sin(a) * r;
    const mr = Math.atan2(my, mx);
    drawMitochondrion(body, mx, my, radius * 0.14, mr);
  }

  // vesicles — tiny bright dots
  for (let i = 0; i < 5; i++) {
    const a = i * 1.37 + time * 0.6;
    const r = radius * (0.3 + 0.15 * Math.sin(time + i));
    body.circle(Math.cos(a) * r, Math.sin(a) * r, radius * 0.055)
      .fill({ color: 0xE6FFFA, alpha: 0.55 });
  }

  if (mutations.includes('reflex_arc')) drawReflexArc(body, radius, time);
  if (mutations.includes('regenerative_tissue')) drawRegenSparkles(body, radius, time);

  // nucleus
  const nucleusDark = mutations.includes('predatory_instinct') ? 0x4a0e0e : 0x1f3a5c;
  body.circle(radius * 0.08, -radius * 0.06, radius * 0.38).fill({ color: nucleusDark, alpha: 0.6 });
  body.circle(radius * 0.08, -radius * 0.06, radius * 0.38).stroke({ color: 0x4FD1C5, alpha: 0.25, width: 1 });
  // nucleolus inside
  body.circle(radius * 0.02, -radius * 0.12, radius * 0.12).fill({ color: 0x0b1e36, alpha: 0.85 });

  // Overlays drawn ON TOP of the membrane: spikes, shell plates, eyespot,
  // toxin sac, cilia, flagellum.
  drawOverlays(body, radius, time, mutations);
}

// ---- per-mutation cosmetic overlays ----

function drawUnderlays(body: Graphics, radius: number, time: number, ids: readonly string[]): void {
  if (ids.includes('magnetic_membrane')) {
    // concentric field rings pulsing outward
    for (let i = 0; i < 3; i++) {
      const phase = (time * 0.6 + i * 0.4) % 1.2;
      const r = radius * (1.4 + phase * 1.2);
      const a = 0.22 * (1 - phase / 1.2);
      body.circle(0, 0, r).stroke({ color: 0x90cdf4, alpha: a, width: 1.2 });
    }
  }
  if (ids.includes('solar_bloom')) {
    // bright green photosynthesis aura that breathes
    const pulse = 0.5 + Math.sin(time * 1.6) * 0.5;
    softHalo(body, radius * (1.45 + pulse * 0.15), 0x9ae6b4, 3, 0.22);
  }
  if (ids.includes('light_harvest')) {
    // golden sun rays around the membrane
    const rayCount = 12;
    for (let i = 0; i < rayCount; i++) {
      const a = (i / rayCount) * Math.PI * 2 + time * 0.15;
      const inner = radius * 1.15;
      const outer = radius * (1.55 + Math.sin(time * 2 + i) * 0.1);
      body.moveTo(Math.cos(a) * inner, Math.sin(a) * inner)
        .lineTo(Math.cos(a) * outer, Math.sin(a) * outer)
        .stroke({ color: 0xfde68a, alpha: 0.4, width: 1.2 });
    }
  }
}

function drawOverlays(body: Graphics, radius: number, time: number, ids: readonly string[]): void {
  if (ids.includes('thick_membrane')) {
    // double-membrane reinforcement ring
    body.circle(0, 0, radius * 1.08).stroke({ color: 0x805ad5, alpha: 0.55, width: 2 });
    body.circle(0, 0, radius * 1.14).stroke({ color: 0xb794f4, alpha: 0.3, width: 1 });
  }
  if (ids.includes('hardened_shell')) {
    // angular polygonal plates around the membrane (radiolarian-style)
    const sides = 8;
    const r1 = radius * 1.18;
    const pts: number[] = [];
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2 + time * 0.05;
      pts.push(Math.cos(a) * r1, Math.sin(a) * r1);
    }
    body.poly(pts).stroke({ color: 0xa78bfa, alpha: 0.7, width: 1.5 });
    // plate seams from each vertex inward
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2 + time * 0.05;
      body.moveTo(Math.cos(a) * r1, Math.sin(a) * r1)
        .lineTo(Math.cos(a) * radius * 0.95, Math.sin(a) * radius * 0.95)
        .stroke({ color: 0x6b46c1, alpha: 0.35, width: 0.8 });
    }
  }
  if (ids.includes('cilia_lash')) {
    // short stiff cilia around the perimeter; lash outward in a pulse
    const count = 18;
    const lash = 0.5 + 0.5 * Math.sin(time * 4);
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const inner = radius * 1.02;
      const outer = radius * (1.18 + lash * 0.18);
      body.moveTo(Math.cos(a) * inner, Math.sin(a) * inner)
        .lineTo(Math.cos(a) * outer, Math.sin(a) * outer)
        .stroke({ color: 0xfc8181, alpha: 0.7, width: 1.1 });
    }
  }
  if (ids.includes('toxin_sac')) {
    // a pair of toxin vesicles on opposite sides — pulsing red-orange
    const pulse = 0.85 + Math.sin(time * 3) * 0.15;
    for (let i = 0; i < 2; i++) {
      const a = i * Math.PI + time * 0.35;
      const tx = Math.cos(a) * radius * 0.78;
      const ty = Math.sin(a) * radius * 0.78;
      const tr = radius * 0.18 * pulse;
      softHalo(body, tr * 1.6, 0xfc8181, 2, 0.28);
      body.circle(tx, ty, tr).fill({ color: 0xfc8181, alpha: 0.8 });
      body.circle(tx - tr * 0.25, ty - tr * 0.25, tr * 0.4).fill({ color: 0xfff5f5, alpha: 0.55 });
    }
  }
  if (ids.includes('predatory_instinct')) {
    // forward eyespot — local +x is the cell's "forward" direction
    body.circle(radius * 0.7, 0, radius * 0.13).fill({ color: 0x1a0000, alpha: 0.85 });
    body.circle(radius * 0.74, -radius * 0.02, radius * 0.07).fill({ color: 0xfc8181, alpha: 0.95 });
    body.circle(radius * 0.76, -radius * 0.04, radius * 0.025).fill({ color: 0xffffff, alpha: 0.9 });
  }
  if (ids.includes('flagellum')) {
    // whip tail trailing behind the cell (-x is "back" in local space)
    const start = -radius * 1.0;
    const end = -radius * 2.6;
    wavyLine(body, start, 0, end, 0, 5, 2.4, time * 12, 0xa0aec0, 0.85, 1.4);
    wavyLine(body, start, 0, end * 0.85, 0, 3.5, 2.4, time * 12 + 0.4, 0xe2e8f0, 0.55, 0.9);
  }
}

function drawChloroplasts(body: Graphics, radius: number, time: number): void {
  // 3 green oval chloroplasts orbiting opposite to the mitochondria
  const count = 3;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 - time * 0.25;
    const r = radius * 0.42;
    const cx = Math.cos(a) * r;
    const cy = Math.sin(a) * r;
    const rot = Math.atan2(cy, cx) + Math.PI / 2;
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const pts: number[] = [];
    const cr = radius * 0.16;
    const steps = 14;
    for (let j = 0; j < steps; j++) {
      const t = (j / steps) * Math.PI * 2;
      const ox = Math.cos(t) * cr * 1.4;
      const oy = Math.sin(t) * cr * 0.6;
      pts.push(cx + ox * cos - oy * sin, cy + ox * sin + oy * cos);
    }
    body.poly(pts).fill({ color: 0x38a169, alpha: 0.75 });
    body.poly(pts).stroke({ color: 0x9ae6b4, alpha: 0.55, width: 0.8 });
    // thylakoid stripes
    body.moveTo(cx - cos * cr * 1.0, cy - sin * cr * 1.0)
      .lineTo(cx + cos * cr * 1.0, cy + sin * cr * 1.0)
      .stroke({ color: 0x276749, alpha: 0.6, width: 0.8 });
  }
}

function drawReflexArc(body: Graphics, radius: number, time: number): void {
  // brief lightning-style synapse arcs between random points inside
  const arcs = 3;
  for (let i = 0; i < arcs; i++) {
    const seed = i * 1.7;
    const fade = 0.4 + 0.6 * Math.abs(Math.sin(time * 5 + seed));
    const a1 = time * 0.8 + seed;
    const a2 = a1 + Math.PI * (0.6 + 0.2 * Math.sin(time + seed));
    const r1 = radius * 0.35;
    const r2 = radius * 0.55;
    const x1 = Math.cos(a1) * r1, y1 = Math.sin(a1) * r1;
    const x2 = Math.cos(a2) * r2, y2 = Math.sin(a2) * r2;
    // jagged midpoint
    const mx = (x1 + x2) / 2 + Math.sin(time * 9 + seed) * radius * 0.08;
    const my = (y1 + y2) / 2 + Math.cos(time * 8 + seed) * radius * 0.08;
    body.moveTo(x1, y1).lineTo(mx, my).lineTo(x2, y2)
      .stroke({ color: 0xfaf089, alpha: 0.7 * fade, width: 1.2 });
  }
}

function drawRegenSparkles(body: Graphics, radius: number, time: number): void {
  // small green-white motes that flicker — "stem-like cells dividing"
  const count = 6;
  for (let i = 0; i < count; i++) {
    const a = i * 1.21 + time * 0.4;
    const r = radius * (0.45 + 0.3 * Math.sin(time * 1.3 + i));
    const flicker = 0.4 + 0.6 * Math.abs(Math.sin(time * 4 + i * 0.9));
    const x = Math.cos(a) * r, y = Math.sin(a) * r;
    body.circle(x, y, radius * 0.05 * flicker).fill({ color: 0x9ae6b4, alpha: 0.85 * flicker });
    body.circle(x, y, radius * 0.025 * flicker).fill({ color: 0xf0fff4, alpha: flicker });
  }
}

function drawMitochondrion(g: Graphics, x: number, y: number, r: number, rot: number): void {
  const cos = Math.cos(rot), sin = Math.sin(rot);
  const pts: number[] = [];
  const steps = 14;
  for (let i = 0; i < steps; i++) {
    const t = (i / steps) * Math.PI * 2;
    // elongated oval
    const ox = Math.cos(t) * r * 1.4;
    const oy = Math.sin(t) * r * 0.6;
    pts.push(x + ox * cos - oy * sin, y + ox * sin + oy * cos);
  }
  g.poly(pts).fill({ color: 0xf6c065, alpha: 0.7 });
  // inner stripe (cristae hint)
  g.moveTo(x - cos * r * 1.0, y - sin * r * 1.0)
    .lineTo(x + cos * r * 1.0, y + sin * r * 1.0)
    .stroke({ color: 0xc97d1d, alpha: 0.5, width: 1 });
}

// ---- food: protein (amino-acid chain) ----

export function drawProtein(body: Graphics, time: number): void {
  body.clear();
  softHalo(body, 5, 0xED64A6, 2, 0.18);
  // 3 connected residues (R-group beads)
  for (let i = 0; i < 3; i++) {
    const dx = (i - 1) * 3.6;
    const dy = Math.sin(time * 2 + i * 0.9) * 0.8;
    body.circle(dx, dy, 2.2).fill({ color: 0xED64A6, alpha: 0.9 });
    body.circle(dx - 0.5, dy - 0.5, 0.9).fill({ color: 0xFED7E2, alpha: 0.7 });
  }
  // connecting peptide bonds
  body.moveTo(-3.6, 0).lineTo(0, 0).lineTo(3.6, 0)
    .stroke({ color: 0xED64A6, alpha: 0.5, width: 1 });
}

// ---- food: sugar (glucose ring) ----

export function drawSugar(body: Graphics): void {
  body.clear();
  softHalo(body, 7, 0xF6AD55, 3, 0.18);
  // hexagonal ring
  const pts: number[] = [];
  const r = 6;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  body.poly(pts).fill({ color: 0xF6AD55, alpha: 0.55 });
  body.poly(pts).stroke({ color: 0xffd492, alpha: 0.9, width: 1.5 });
  // O at one corner (carbohydrate oxygen hint)
  body.circle(0, -r, 1.5).fill({ color: 0xffeac1, alpha: 0.95 });
  // inner sparkle
  body.circle(0, 0, 2).fill({ color: 0xffe0a8, alpha: 0.5 });
}

// ---- food: bacteria (bacillus rod with flagellum) ----

export function drawBacteria(body: Graphics, time: number): void {
  body.clear();
  softHalo(body, 10, 0x68D391, 2, 0.14);
  // rod body
  const len = 18, wid = 6;
  const pts: number[] = [];
  const steps = 20;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = t * Math.PI; // top half
    pts.push(-len / 2 + len * t, -Math.sin(a) * wid * 0.5);
  }
  for (let i = steps; i >= 0; i--) {
    const t = i / steps;
    const a = t * Math.PI;
    pts.push(-len / 2 + len * t, Math.sin(a) * wid * 0.5);
  }
  body.poly(pts).fill({ color: 0x3a8b52, alpha: 0.4 });
  // inner core (nucleoid)
  body.ellipse(0, 0, len * 0.4, wid * 0.3).fill({ color: 0x68D391, alpha: 0.85 });
  body.ellipse(0, 0, len * 0.2, wid * 0.15).fill({ color: 0xC6F6D5, alpha: 0.7 });
  // flagellum — a long wavy tail behind it
  wavyLine(body, -len / 2, 0, -len / 2 - 16, 0, 3.5, 2.2, time * 10, 0x68D391, 0.7, 1.2);
}

// ---- food: DNA (double helix) ----

export function drawDNA(body: Graphics, time: number): void {
  body.clear();
  // purple glow halo
  for (let i = 0; i < 5; i++) {
    const a = 0.09 - i * 0.014;
    if (a <= 0) break;
    body.circle(0, 0, 7 + i * 3).fill({ color: 0xB794F4, alpha: a });
  }
  const len = 18;
  const steps = 18;
  const prev: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = -len / 2 + t * len;
    const y1 = Math.sin(t * Math.PI * 3 + time * 2) * 4;
    const y2 = Math.sin(t * Math.PI * 3 + time * 2 + Math.PI) * 4;
    if (i > 0) {
      const [px1, py1, px2, py2] = [prev[0][0], prev[0][1], prev[1][0], prev[1][1]];
      body.moveTo(px1, py1).lineTo(x, y1).stroke({ color: 0xB794F4, alpha: 0.85, width: 1.4 });
      body.moveTo(px2, py2).lineTo(x, y2).stroke({ color: 0xD6BCFA, alpha: 0.8, width: 1.4 });
    }
    prev[0] = [x, y1]; prev[1] = [x, y2];
    // base pair rungs every other step
    if (i % 2 === 0) {
      body.moveTo(x, y1).lineTo(x, y2).stroke({ color: 0xE9D8FD, alpha: 0.35, width: 0.8 });
    }
    // nucleotide dots
    body.circle(x, y1, 1.2).fill({ color: 0xB794F4, alpha: 0.95 });
    body.circle(x, y2, 1.2).fill({ color: 0xD6BCFA, alpha: 0.95 });
  }
}

// ---- predator: amoeba (pseudopods) ----

export interface PredatorDrawOpts { radius: number; color: number; }

export function drawAmoeba(body: Graphics, opts: PredatorDrawOpts, time: number): void {
  body.clear();
  const { radius, color } = opts;
  softHalo(body, radius * 1.2, color, 3, 0.12);
  // irregular blob with pulsing pseudopods
  const samples = 40;
  const pts: number[] = [];
  const lobeFreq = 5;
  for (let i = 0; i < samples; i++) {
    const a = (i / samples) * Math.PI * 2;
    const lobe = Math.sin(a * lobeFreq + time * 0.9) * 0.22;
    const wobble = Math.sin(a * 2 + time * 0.5) * 0.06;
    const r = radius * (1 + lobe + wobble);
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  body.poly(pts).fill({ color, alpha: 0.35 });
  // inner core (slightly smaller, simpler blob)
  const core: number[] = [];
  for (let i = 0; i < samples; i++) {
    const a = (i / samples) * Math.PI * 2;
    const r = radius * (0.68 + Math.sin(a * 3 + time) * 0.05);
    core.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  body.poly(core).fill({ color: mix(color, 0x000000, 0.2), alpha: 0.75 });
  // food vacuoles inside (dark circles)
  for (let i = 0; i < 3; i++) {
    const a = i * 2.1 + time * 0.2;
    const r = radius * 0.35;
    body.circle(Math.cos(a) * r, Math.sin(a) * r, radius * 0.13)
      .fill({ color: mix(color, 0x000000, 0.45), alpha: 0.7 });
  }
  // nucleus
  body.circle(0, 0, radius * 0.22).fill({ color: 0x1a0a0a, alpha: 0.6 });
  body.circle(radius * 0.04, -radius * 0.04, radius * 0.09).fill({ color: 0x000000, alpha: 0.6 });
}

// ---- predator: paramecium-hunter (ciliate) ----

export function drawParamecium(body: Graphics, opts: PredatorDrawOpts, time: number): void {
  body.clear();
  const { radius, color } = opts;
  softHalo(body, radius * 1.25, color, 3, 0.12);

  // elongated slipper-shape body (asymmetric ellipse)
  const steps = 36;
  const pts: number[] = [];
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    // narrower at back (left)
    const stretch = 1.3 + Math.cos(a) * 0.15;
    const r = radius * 0.95 * (1 + Math.sin(a * 2 + time) * 0.03);
    pts.push(Math.cos(a) * r * stretch, Math.sin(a) * r * 0.65);
  }
  body.poly(pts).fill({ color, alpha: 0.75 });
  // darker ventral side
  body.ellipse(0, radius * 0.18, radius * 1.1, radius * 0.45)
    .fill({ color: mix(color, 0x000000, 0.3), alpha: 0.35 });

  // oral groove
  body.ellipse(radius * 0.45, radius * 0.05, radius * 0.22, radius * 0.14)
    .fill({ color: mix(color, 0x000000, 0.6), alpha: 0.7 });

  // macronucleus
  body.circle(-radius * 0.15, -radius * 0.1, radius * 0.22).fill({ color: 0x2b0b0b, alpha: 0.55 });
  body.circle(-radius * 0.12, -radius * 0.1, radius * 0.1).fill({ color: 0x0a0000, alpha: 0.7 });
  // micronucleus
  body.circle(-radius * 0.35, radius * 0.05, radius * 0.08).fill({ color: 0x2b0b0b, alpha: 0.55 });

  // contractile vacuoles
  body.circle(radius * 0.7, -radius * 0.2, radius * 0.1).fill({ color: mix(color, 0xffffff, 0.5), alpha: 0.4 });
  body.circle(-radius * 0.7, -radius * 0.2, radius * 0.1).fill({ color: mix(color, 0xffffff, 0.5), alpha: 0.4 });

  // cilia around the edge
  const ciliaCount = 44;
  for (let i = 0; i < ciliaCount; i++) {
    const a = (i / ciliaCount) * Math.PI * 2;
    const bx = Math.cos(a) * radius * 1.28;
    const by = Math.sin(a) * radius * 0.83;
    // cilia beat in metachronal wave
    const wavePhase = a * 3 + time * 9;
    const beat = Math.sin(wavePhase);
    const len = 4 + beat * 3;
    // each cilium extends outward + tangential bend
    const tx = -Math.sin(a), ty = Math.cos(a);
    const ex = bx + Math.cos(a) * len + tx * beat * 2;
    const ey = by + Math.sin(a) * len * 0.7 + ty * beat * 2;
    body.moveTo(bx, by).lineTo(ex, ey).stroke({ color, alpha: 0.55, width: 1 });
  }
}

// ---- predator: armored dinoflagellate ----

export function drawDinoflagellate(body: Graphics, opts: PredatorDrawOpts, time: number): void {
  body.clear();
  const { radius, color } = opts;
  softHalo(body, radius * 1.15, color, 3, 0.12);

  // two halves: epitheca (top) and hypotheca (bottom), separated by a cingulum
  const r = radius;
  const upperPts: number[] = [];
  const lowerPts: number[] = [];
  const sides = 7;
  for (let i = 0; i <= sides; i++) {
    const t = i / sides;
    const a = Math.PI * t - Math.PI; // from -π to 0 (top arc)
    upperPts.push(Math.cos(a) * r, Math.sin(a) * r * 0.9);
  }
  for (let i = 0; i <= sides; i++) {
    const t = i / sides;
    const a = Math.PI * t; // 0 to π (bottom arc)
    lowerPts.push(Math.cos(a) * r, Math.sin(a) * r * 1.1);
  }
  // epitheca (top)
  body.poly(upperPts).fill({ color: mix(color, 0xffffff, 0.1), alpha: 0.85 });
  body.poly(upperPts).stroke({ color: mix(color, 0x000000, 0.3), alpha: 0.8, width: 1 });
  // hypotheca (bottom)
  body.poly(lowerPts).fill({ color, alpha: 0.9 });
  body.poly(lowerPts).stroke({ color: mix(color, 0x000000, 0.3), alpha: 0.8, width: 1 });

  // theca plate lines (running from cingulum outward)
  for (let i = 1; i < sides; i++) {
    const t = i / sides;
    const aTop = Math.PI * t - Math.PI;
    const aBot = Math.PI * t;
    body.moveTo(0, 0).lineTo(Math.cos(aTop) * r * 0.95, Math.sin(aTop) * r * 0.85)
      .stroke({ color: mix(color, 0x000000, 0.5), alpha: 0.5, width: 0.8 });
    body.moveTo(0, 0).lineTo(Math.cos(aBot) * r * 0.95, Math.sin(aBot) * r * 1.05)
      .stroke({ color: mix(color, 0x000000, 0.5), alpha: 0.5, width: 0.8 });
  }

  // equatorial cingulum (band around the waist)
  body.rect(-r * 1.02, -r * 0.1, r * 2.04, r * 0.2).fill({ color: mix(color, 0x000000, 0.55), alpha: 0.55 });

  // two flagella — transverse (wraps around the cingulum, shown as dashed band)
  for (let i = 0; i < 8; i++) {
    const t = i / 8;
    const x = -r * 1.02 + t * r * 2.04;
    const y = Math.sin(time * 6 + t * 8) * 2;
    body.circle(x, y, 0.8).fill({ color: mix(color, 0xffffff, 0.5), alpha: 0.6 });
  }
  // longitudinal flagellum trailing behind
  wavyLine(body, 0, r, 0, r * 2.2, 4, 2.5, time * 6, color, 0.7, 1.2);

  // pigment spot (eyespot)
  body.circle(r * 0.3, -r * 0.35, r * 0.08).fill({ color: 0xffec8b, alpha: 0.85 });
}
